import { supabase } from './supabase'

export interface ProposalLineDraft {
  label: string
  amount: number
  category?: string
  editable?: boolean
}

export interface SavePatientProposalInput {
  orgId: string
  sourceApp: 'or' | 'surgery' | 'revela' | 'forge' | 'api'
  encounterId?: number | string | null
  patientId?: number | string | null
  linkedCaseId?: number | string | null
  patientName?: string
  patientEmail?: string
  facilityId?: number | string | null
  facilityName?: string | null
  providerId?: number | string | null
  providerName?: string | null
  procedureName: string
  procedureType?: string
  estimatedDurationHours?: number
  patientSummary?: string
  lineItems: ProposalLineDraft[]
  financingOptions?: unknown[]
  selectedFinancingIndex?: number
  aiGenerated?: boolean
  proposalSnapshot?: Record<string, unknown>
}

function nullableString(value: unknown): string | null {
  if (value === undefined || value === null || value === '') return null
  return String(value)
}

export async function savePatientProposal(input: SavePatientProposalInput) {
  const subtotal = input.lineItems.reduce((sum, item) => sum + Number(item.amount || 0), 0)

  const payload = {
    org_id: input.orgId,
    source_app: input.sourceApp,
    encounter_id: nullableString(input.encounterId),
    patient_id: nullableString(input.patientId),
    linked_case_id: nullableString(input.linkedCaseId),
    patient_display_name: input.patientName || null,
    patient_email: input.patientEmail || null,
    facility_id: nullableString(input.facilityId),
    facility_name: input.facilityName || null,
    provider_id: nullableString(input.providerId),
    provider_name: input.providerName || null,
    procedure_name: input.procedureName,
    procedure_type: input.procedureType || null,
    estimated_duration_hours: input.estimatedDurationHours ?? null,
    status: 'draft',
    subtotal,
    discount_total: 0,
    tax_total: 0,
    grand_total: subtotal,
    deposit_required: 0,
    balance_due: subtotal,
    selected_financing_index: input.selectedFinancingIndex ?? 0,
    financing_options: input.financingOptions ?? [],
    patient_summary: input.patientSummary || null,
    proposal_snapshot: input.proposalSnapshot ?? {},
    ai_generated: input.aiGenerated ?? true,
    line_items: input.lineItems.map((item, index) => ({
      label: item.label,
      amount: Number(item.amount || 0),
      category: item.category || 'proposal',
      line_type: item.category || 'fee',
      quantity: 1,
      patient_visible: true,
      internal_only: false,
      sort_order: index,
      editable: item.editable ?? true,
    })),
  }

  const { data, error } = await supabase.rpc('save_patient_proposal', { p_payload: payload })
  if (error) throw error

  const row = Array.isArray(data) ? data[0] : data
  if (!row?.proposal_id) throw new Error('Proposal save did not return a proposal_id')
  return row as { proposal_id: string; proposal_number: string }
}
